import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch, FaLocationArrow, FaSpinner, FaBars, FaSlidersH,
  FaTimes, FaHome, FaUser, FaCheckCircle, FaHdd, FaStar, FaPowerOff, FaDownload,
} from 'react-icons/fa';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import usePwaInstall from '../hooks/usePwaInstall';
import '../styles/Header.css';

function Header({ user, userName, searchQuery, setSearchQuery, handleSearch, getCurrentLocation, locationLoading, onFilterClick, mapCenter }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { canInstall, isInstalled, isIos, promptInstall } = usePwaInstall();
  const [iosHintVisible, setIosHintVisible] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const handleInstall = async () => {
    if (canInstall) {
      await promptInstall();
      closeMenu();
    } else if (isIos) {
      setIosHintVisible(true);
    }
  };
  const showInstall = !isInstalled && (canInstall || isIos);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    { icon: FaHome,        label: t('menu.home'),               onClick: () => { navigate('/homepage'); closeMenu(); } },
    { icon: FaUser,        label: t('menu.my_profile'),         onClick: () => { navigate('/profile'); closeMenu(); } },
    { icon: FaCheckCircle, label: t('menu.completed_routes'),   onClick: null, badge: null },
    { icon: FaHdd,         label: t('menu.manage_offline'),     onClick: () => { navigate('/offline-content'); closeMenu(); } },
    { icon: FaStar,        label: t('menu.suggested_routes'),   onClick: () => { navigate('/suggested-routes', { state: { mapCenter } }); closeMenu(); } },
  ];

  return (
    <div className="header-container">
      <div className="homepage-header">
        <div className="user-greeting">
          <div className="user-avatar">
            {user?.photoURL ? (
              <img src={user.photoURL} alt="Profile" />
            ) : (
              <div className="avatar-placeholder">
                {userName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="greeting-text">
            <h2>{t('greeting', { name: userName })}</h2>
            <p>{t('where_to_go')}</p>
          </div>
        </div>
        <div className="hamburger-menu" onClick={() => setMenuOpen(true)}>
          <FaBars />
        </div>
      </div>

      {menuOpen && (
        <div className="menu-overlay" onClick={closeMenu}>
          <div className="menu-content" onClick={(e) => e.stopPropagation()}>

            {/* Close button */}
            <button className="menu-close-btn" onClick={closeMenu}>
              <FaTimes />
            </button>

            {/* Logo + app name */}
            <div className="menu-brand">
              <img src="icons/icon-512.png" alt="Logo" className="menu-logo" />
              <span className="menu-app-name">WHEREITHAPPENED</span>
            </div>

            <div className="menu-divider" />

            {/* User info */}
            <div className="menu-user">
              <div className="menu-user-avatar">
                {user?.photoURL ? (
                  <img src={user.photoURL} alt="Profile" />
                ) : (
                  <div className="menu-avatar-placeholder">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="menu-user-info">
                <span className="menu-user-name">{userName}</span>
                <span className="menu-user-email">{user?.email}</span>
              </div>
            </div>

            <div className="menu-divider" />

            {/* Nav items */}
            <nav className="menu-nav">
              {menuItems.map(({ icon: Icon, label, onClick, badge }) => (
                <button
                  key={label}
                  className={`menu-item${onClick ? '' : ' menu-item--disabled'}`}
                  onClick={onClick ?? undefined}
                  disabled={!onClick}
                >
                  <Icon className="menu-item-icon" />
                  <span className="menu-item-label">{label}</span>
                  {badge != null && (
                    <span className="menu-item-badge">{badge}</span>
                  )}
                </button>
              ))}
            </nav>

            <div className="menu-divider" />

            {showInstall && (
              <button className="menu-item menu-item--install" onClick={handleInstall}>
                <FaDownload className="menu-item-icon" />
                <span className="menu-item-label">{t('menu.install_app')}</span>
              </button>
            )}

            {iosHintVisible && (
              <div className="menu-ios-hint" role="status">
                {t('install.ios_hint')}
              </div>
            )}

            {/* Logout */}
            <button className="menu-item menu-item--logout" onClick={handleLogout}>
              <FaPowerOff className="menu-item-icon" />
              <span className="menu-item-label">{t('menu.log_out')}</span>
            </button>

          </div>
        </div>
      )}

      <div className="search-container">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder={t('search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyUp={handleSearch}
            onClick={(e) => e.target.select()}
          />
          <button
            className="location-button"
            onClick={getCurrentLocation}
            disabled={locationLoading}
            title={locationLoading ? t('getting_location') : t('current_location')}
          >
            {locationLoading ? <FaSpinner className="spinning" /> : <FaLocationArrow />}
          </button>
        </div>
        <button className="filter-button" onClick={onFilterClick}>
          <FaSlidersH />
        </button>
      </div>
    </div>
  );
}

export default Header;
